import type {
  ContentItem,
  ContentItemId,
  Device,
  RemoteCommandId,
  RemoteCommandType,
  ScreenTimeBudget,
} from '@kidshub/contracts';
import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toMinutes } from '../lib/duration.js';
import { toErrorKey } from '../lib/error-code.js';
import { formatLastSeen } from '../lib/relative-time.js';
import { toInitial } from '../lib/turkish-case.js';
import { Button, Icon } from '../ui/kit.js';
import { toBeadString } from './bead-string.js';
import { type CommandOutcome, isOutcomeLost, toOutcomeKey } from './command-outcome.js';
import { projectRemaining } from './live-countdown.js';
import { PlayNowSheet } from './play-now-sheet.js';
import { isPresenceAway, PRESENCE_PILL } from './presence-display.js';
import { useLiveDeviceState } from './use-live-device-state.js';

/**
 * What is happening right now, and the three things a parent can do about it.
 *
 * The one ink surface on the page, at the top, because it answers the question
 * a parent opens the dashboard to ask. The left half is the child's day — time
 * left, as a number and as the tablet's bead string; the right half is the
 * tablet and what it is playing, with the controls beside it.
 *
 * The actions are honest about their limits. There is no optimistic state and
 * no spinner that resolves into silence: a command produces a sentence saying
 * what became of it, including the case where it went nowhere.
 */

interface LiveBlockProps {
  device: Device;
  childName: string | null;
  /** The active child's budget, for the between-polls countdown. */
  budget: ScreenTimeBudget | null;
  /** What is playing, if the library knows it. Only the title and the picture are drawn. */
  itemFor: (contentItemId: ContentItemId) => Pick<ContentItem, 'title' | 'posterUrl'> | null;
  /** What the active child may watch, for "open this now". */
  playable: readonly Pick<ContentItem, 'id' | 'title' | 'posterUrl'>[];
}

/** What the board's "+5 dk" button grants. */
const ADD_TIME_SECONDS = 5 * 60;

export const LiveBlock = ({ device, childName, budget, itemFor, playable }: LiveBlockProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { state, polledAtMs, nowMs, errorKey, refresh } = useLiveDeviceState(device.id);
  const [outcome, setOutcome] = useState<CommandOutcome | null>(null);
  const [commandErrorKey, setCommandErrorKey] = useState<string | null>(null);
  const [isIssuing, setIsIssuing] = useState(false);
  const [isPicking, setIsPicking] = useState(false);

  const issue = async (
    type: RemoteCommandType,
    extra: { seconds?: number; contentItemId?: ContentItemId } = {},
  ) => {
    setCommandErrorKey(null);
    setOutcome(null);
    setIsIssuing(true);

    try {
      const result = await apiClient.commands.issue({
        // Minted here so that a retry after a lost response cannot grant the
        // same five minutes twice.
        commandId: crypto.randomUUID() as RemoteCommandId,
        deviceId: device.id,
        type,
        ...extra,
      });
      setOutcome(result);
      refresh();
    } catch (error) {
      setCommandErrorKey(toErrorKey(error));
    } finally {
      setIsIssuing(false);
    }
  };

  const session = state?.activeSession ?? null;
  const secondsSincePoll = Math.floor((nowMs - polledAtMs) / 1000);
  const advancedSeconds = session === null ? 0 : Math.max(secondsSincePoll, 0);

  const countdown =
    state === null || budget === null
      ? null
      : projectRemaining({
          serverRemainingSeconds: state.remainingSeconds,
          serverLimitingFactor: state.limitingFactor,
          secondsSincePoll,
          isSessionRunning: session !== null,
          budget,
          contentType: session?.contentType ?? 'VIDEO',
          spentTodaySeconds: state.spentTodaySeconds,
          sessionElapsedSeconds: session?.elapsedSeconds ?? 0,
        });

  const beads =
    countdown === null || budget === null
      ? null
      : toBeadString({
          remainingSeconds: countdown.remainingSeconds,
          dailyTotalSeconds: budget.dailyTotalSeconds,
        });

  const elapsedSeconds = session === null ? 0 : session.elapsedSeconds + advancedSeconds;
  const watching =
    session !== null && session.contentItemId !== null ? itemFor(session.contentItemId) : null;
  const isOnline = state?.isOnline === true;
  const subject = childName ?? device.name;
  const lastSeenAt = state?.lastSeenAt ?? device.lastSeenAt;

  return (
    <section className="now" aria-label={device.name}>
      <div className="now-zone">
        <div className="now-who">
          <span className="avatar" aria-hidden="true">
            {childName === null ? <Icon name="tablet" small /> : toInitial(childName, locale)}
          </span>
          <b>{t('live.childToday', { name: subject })}</b>
          {state === null ? null : (
            <span className={`pill ${PRESENCE_PILL[state.presence]}`}>
              <span className="dot" />
              {t(`live.presence.${state.presence}`)}
            </span>
          )}
        </div>

        {countdown === null || budget === null ? null : (
          <div>
            <p className="remain">
              <span className="t-display">{toMinutes(countdown.remainingSeconds)}</span>
              <span className="remain-u">{t('live.remainingUnit')}</span>
            </p>
            <p className="remain-of">
              {t('live.dailyBudget', { minutes: toMinutes(budget.dailyTotalSeconds) })} {/*
               * Refused, the figure is zero for a reason rather than a limit:
               * outside the allowed hours it is the hours, whatever budget is
               * left, and naming a limit there was naming the wrong one.
               */}
              {state?.reason === undefined || state.reason === 'ALLOWED'
                ? t('live.limitedBy', { limit: t(`limit.${countdown.limitingFactor}`) })
                : `· ${t(`gateReason.${state.reason}`)}`}
            </p>
          </div>
        )}

        {beads === null || beads.fills.length === 0 || countdown === null ? null : (
          <div
            className="beads"
            role="img"
            aria-label={t('live.beadsLabel', {
              minutes: toMinutes(countdown.remainingSeconds),
              beadMinutes: toMinutes(beads.beadSeconds),
            })}
          >
            {beads.fills.map((fill, index) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: a bead has no identity beyond its place
                key={index}
                className={fill === 1 ? 'bead is-full' : 'bead'}
              >
                <i style={{ '--f': `${Math.round(fill * 100)}%` } as CSSProperties} />
              </span>
            ))}
          </div>
        )}

        {state === null ? null : (
          <div className="facts">
            {session === null || budget === null ? null : (
              <p className="fact">
                <Icon name="hourglass" small />
                <span>
                  {t('live.sessionFact', {
                    elapsed: toMinutes(elapsedSeconds),
                    max: toMinutes(budget.maxSessionSeconds),
                    rest: toMinutes(budget.restPeriodSeconds),
                  })}
                </span>
              </p>
            )}
            <p className="fact">
              <Icon name="sun" small />
              <span>
                {t('live.spentToday', {
                  minutes: toMinutes(state.spentTodaySeconds + advancedSeconds),
                })}
              </span>
            </p>
          </div>
        )}
      </div>

      <div className="now-zone">
        <p className="dev-line">
          <Icon name="tablet" small />
          <b>{device.name}</b>
          {isOnline || state === null || !isPresenceAway(state.presence) ? (
            <span className={isOnline ? 'ok' : 'off'}>
              <span className="dot" />
              {t(isOnline ? 'devices.online' : 'devices.offline')}
            </span>
          ) : (
            // Gone quiet: say which kind of quiet, and since when, so a parent
            // can tell a closed app from a tablet that lost its connection.
            <span className="away">
              <span className="dot" />
              {t(`live.presence.${state.presence}`)}
              {lastSeenAt === null
                ? null
                : ` · ${t('devices.lastSeen', { when: formatLastSeen(lastSeenAt, locale) })}`}
            </span>
          )}
        </p>

        {session === null ? (
          <p className="rested">
            <b>{t('live.nothingPlaying')}</b>
            <span>{t(`gateReason.${state?.reason ?? 'ALLOWED'}`)}</span>
          </p>
        ) : (
          <div className="playing">
            <span className="poster overview-poster">
              {watching === null || watching.posterUrl === null ? (
                <Icon name="play" />
              ) : (
                <img src={watching.posterUrl} alt="" />
              )}
            </span>
            <p className="playing-t">
              {watching?.title ?? t(`contentType.${session.contentType}`)}
              <span>{t('live.elapsed', { minutes: toMinutes(elapsedSeconds) })}</span>
            </p>
          </div>
        )}

        <div className="now-actions">
          {/*
           * Pause and resume are one control. The redesign drew only "pause",
           * and a paused tablet then had no way back from the panel: the child
           * could open nothing, whatever the budget said.
           */}
          {state?.reason === 'PARENT_PAUSED' ? (
            <Button
              variant="amber"
              icon="play"
              disabled={isIssuing}
              onClick={() => void issue('RESUME')}
            >
              {t('live.resume')}
            </Button>
          ) : (
            <Button
              variant="amber"
              icon="pause"
              disabled={isIssuing}
              onClick={() => void issue('PAUSE')}
            >
              {t('live.pause')}
            </Button>
          )}
          {/* On ink the secondary actions have no kit variant; system.css draws them. */}
          <button
            type="button"
            className="btn btn-onink"
            disabled={isIssuing}
            onClick={() => void issue('ADD_TIME', { seconds: ADD_TIME_SECONDS })}
          >
            {t('live.addTime', { minutes: toMinutes(ADD_TIME_SECONDS) })}
          </button>
          <button
            type="button"
            className="btn btn-onink span"
            disabled={isIssuing || playable.length === 0}
            onClick={() => setIsPicking(true)}
          >
            <Icon name="play" small />
            {t('live.playNow')}
          </button>
          <button
            type="button"
            className="btn btn-onink-danger span"
            disabled={isIssuing}
            onClick={() => void issue('END_SESSION')}
          >
            <Icon name="stop" small />
            {t('live.endSession')}
          </button>
        </div>

        <div className="now-notes" role="status">
          {outcome === null ? null : (
            <p className={isOutcomeLost(outcome) ? 'now-note is-lost' : 'now-note'}>
              {t(toOutcomeKey(outcome))}
            </p>
          )}

          {commandErrorKey === null ? null : (
            <p className="now-note is-lost">{t(commandErrorKey)}</p>
          )}

          {errorKey === null ? null : <p className="now-note">{t('live.staleView')}</p>}
        </div>
      </div>
      {isPicking ? (
        <PlayNowSheet
          items={playable}
          onClose={() => setIsPicking(false)}
          onPick={(item) => {
            setIsPicking(false);
            void issue('PLAY_CONTENT', { contentItemId: item.id });
          }}
        />
      ) : null}
    </section>
  );
};
